/**
=========================================================
* Soft UI Dashboard React - v3.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import { useEffect, useRef, useState, memo, useCallback } from "react";
// react-router-dom components
import { useLocation, NavLink, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

import clsx from "clsx";
// @mui material components
import List from "@mui/material/List";
import Icon from "@mui/material/Icon";
// Soft UI Dashboard React components
import SuiBox from "components/SuiBox";
import { getLocalToken, getLocalRoles } from "utils/storage";
// Soft UI Dashboard React examples
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";

// Soft UI Dashboard React context
import { Typography } from "@mui/material";
import { useSoftUIController } from "context";
import { setMiniSidenav } from "context/theme/action";
import SampleLogo from "assets/images/logos/LogoZumi.png";
import { setLoading } from "context/common/action";
import { API, standardResponse } from "api/middleware";
import style from "./style.module.css";
import "./style.css";
import { useColorTheme } from "../../context/ColorThemeContext";

// Memoized MenuItem to prevent unnecessary re-renders of individual items
const MenuItem = memo(
  ({
    item,
    level,
    pathname,
    collapseName,
    close,
    hideState,
    toggleHideState,
    checkForActiveChildren,
    MemoizedSidenavCollapse,
    renderMenu,
    miniSidenav,
  }) => {
    const colors = useColorTheme();
    const { menu, child } = item;
    if (!menu || !Array.isArray(child)) {
      console.warn("Invalid menu item:", item);
      return null;
    }

    const key = menu.menuId.toString();
    const isActive = pathname === menu.link || collapseName === key;
    const hasActiveChild = child.length > 0 && checkForActiveChildren(child);

    // Memoize the submenu rendering to avoid unnecessary re-renders
    const renderSubmenu = useCallback(() => {
      if (child.length === 0) return null;
      return renderMenu(child, level + 1);
    }, [child, level, renderMenu]);

    if (child.length === 0) {
      return (
        <NavLink to={menu.link || "#"} key={key}>
          <MemoizedSidenavCollapse
            color="info"
            name={menu.title}
            icon={menu.icon}
            active={isActive}
            noCollapse={true}
            close={close}
            level={level}
            style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              overflow: "hidden",
              maxHeight: "58px",
              ...(colors.sideText && { color: colors.sideText }),
            }}
          />
        </NavLink>
      );
    }

    return (
      <div
        key={key}
        className={`nav-item ${isActive ? "active" : ""} ${
          hasActiveChild ? "parent-of-active" : ""
        }`}
        role="menuitem"
        tabIndex={0}
      >
        <SuiBox
          className={clsx(style.sidenavCollapse)}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          fontWeight="bold"
          onClick={() => toggleHideState(key)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              toggleHideState(key);
            }
          }}
          sx={{
            "&&:hover": {
              backgroundColor: "#0094FF",
              transition: "none",
            },
          }}
        >
          <MemoizedSidenavCollapse
            name={menu.title}
            icon={menu.icon}
            active={isActive}
            close={close}
            hasChildren={true}
            level={level}
            isParentOfActive={hasActiveChild}
            style={{ ...(colors.sideText && { color: colors.sideText }) }}
          />
          {!(miniSidenav && close) && (
            <Icon
              className="submenu-arrow"
              sx={{
                transition: "transform 0.3s",
                transform: hideState[key] ? "rotate(90deg)" : "rotate(0deg)",
                fontSize: "8px",
                marginRight: "10px",
                color: "#3c7363 !important",
                position: "relative",
                zIndex: 1,
              }}
            >
              keyboard_arrow_right
            </Icon>
          )}
        </SuiBox>

        {(!miniSidenav || !close) && hideState[key] && (
          <div className="submenu-container">{renderSubmenu()}</div>
        )}
      </div>
    );
  },
  (prevProps, nextProps) =>
    prevProps.item === nextProps.item &&
    prevProps.pathname === nextProps.pathname &&
    prevProps.collapseName === nextProps.collapseName &&
    prevProps.close === nextProps.close &&
    prevProps.miniSidenav === nextProps.miniSidenav &&
    prevProps.hideState[prevProps.item.menu.menuId] ===
      nextProps.hideState[nextProps.item.menu.menuId]
);

MenuItem.displayName = "MenuItem";

MenuItem.propTypes = {
  item: PropTypes.shape({
    menu: PropTypes.shape({
      menuId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      link: PropTypes.string,
      icon: PropTypes.string,
    }),
    child: PropTypes.arrayOf(
      PropTypes.shape({
        menu: PropTypes.shape({
          menuId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          title: PropTypes.string,
          link: PropTypes.string,
          icon: PropTypes.string,
        }),
        child: PropTypes.arrayOf(PropTypes.shape({})), // Nested children
      })
    ),
  }).isRequired,
  level: PropTypes.number.isRequired,
  pathname: PropTypes.string.isRequired,
  collapseName: PropTypes.string.isRequired,
  close: PropTypes.bool.isRequired,
  hideState: PropTypes.objectOf(PropTypes.bool).isRequired,
  toggleHideState: PropTypes.func.isRequired,
  checkForActiveChildren: PropTypes.func.isRequired,
  MemoizedSidenavCollapse: PropTypes.elementType.isRequired,
  renderMenu: PropTypes.func.isRequired,
  miniSidenav: PropTypes.bool.isRequired,
};

MenuItem.defaultProps = {};

// Memoized MenuRenderer to prevent unnecessary re-renders of the menu
const MenuRenderer = memo(
  ({
    items,
    level,
    pathname,
    collapseName,
    close,
    hideState,
    toggleHideState,
    checkForActiveChildren,
    MemoizedSidenavCollapse,
    renderMenu,
    miniSidenav,
  }) =>
    items.map((item) => (
      <MenuItem
        key={item.menu.menuId}
        item={item}
        level={level}
        pathname={pathname}
        collapseName={collapseName}
        close={close}
        hideState={hideState}
        toggleHideState={toggleHideState}
        checkForActiveChildren={checkForActiveChildren}
        MemoizedSidenavCollapse={MemoizedSidenavCollapse}
        renderMenu={renderMenu}
        miniSidenav={miniSidenav}
      />
    )),
  (prevProps, nextProps) =>
    prevProps.items === nextProps.items &&
    prevProps.pathname === nextProps.pathname &&
    prevProps.collapseName === nextProps.collapseName &&
    prevProps.miniSidenav === nextProps.miniSidenav &&
    prevProps.close === nextProps.close &&
    prevProps.hideState === nextProps.hideState
);

MenuRenderer.defaultProps = {};

MenuRenderer.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      menu: PropTypes.shape({
        menuId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        title: PropTypes.string.isRequired,
        link: PropTypes.string,
        icon: PropTypes.string,
      }),
      child: PropTypes.arrayOf(
        PropTypes.shape({
          menu: PropTypes.shape({
            menuId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            title: PropTypes.string,
            link: PropTypes.string,
            icon: PropTypes.string,
          }),
          child: PropTypes.arrayOf(PropTypes.shape({})), // Nested children
        })
      ),
    })
  ).isRequired,
  level: PropTypes.number.isRequired,
  pathname: PropTypes.string.isRequired,
  collapseName: PropTypes.string.isRequired,
  close: PropTypes.bool.isRequired,
  hideState: PropTypes.objectOf(PropTypes.bool).isRequired,
  toggleHideState: PropTypes.func.isRequired,
  checkForActiveChildren: PropTypes.func.isRequired,
  MemoizedSidenavCollapse: PropTypes.elementType.isRequired,
  renderMenu: PropTypes.func.isRequired,
  miniSidenav: PropTypes.bool.isRequired,
};

function Sidenav({ color, brand, brandName, ...rest }) {
  // const [showLogo, setShowLogo] = useState(true);
  const [menuItems, setMenuItems] = useState(null);
  const navigate = useNavigate();
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentSidenav } = controller.theme;
  const location = useLocation();
  const { pathname } = location;
  const pathNameArr = pathname.split("/");
  const collapseName = pathNameArr[pathNameArr.length - 1];
  const [close, setClose] = useState(false);
  const [smallScreen, setSmallScreen] = useState(true);
  const [hideState, setHideState] = useState({});
  const navRef = useRef();
  const [isPinnedOpen, setIsPinnedOpen] = useState(false);
  const hideStateBeforeMini = useRef({});
  const colors = useColorTheme();

  const toggleHideState = useCallback(
    (key) => {
      setHideState((prev) => ({
        ...prev,
        [key]: !prev[key],
      }));
    },
    [setHideState]
  );
  const initialHideRef = useRef({});

  useEffect(() => {
    const fetchMenu = async () => {
      const url = "/api/Menu/list";
      try {
        const response = await API.get(url);
        const result = standardResponse(true, response);
        const menuData = result.message.data?.object || [];

        const validMenuData = menuData.filter((item) => {
          const { menu, child } = item;
          if (!menu) return false;

          const userRoles = getLocalRoles();
          const hasPermission = !menu.permissionId || userRoles.includes(menu.permissionId);

          const filterChildren = (children) =>
            children.filter((childItem) => {
              const { menu: childMenu, child: grandChildren } = childItem;
              if (!childMenu) return false;

              const childHasPermission =
                !childMenu.permissionId || userRoles.includes(childMenu.permissionId);

              if (grandChildren && grandChildren.length > 0) {
                childItem.child = filterChildren(grandChildren);
              }

              return childHasPermission || (childItem.child && childItem.child.length > 0);
            });

          if (child && child.length > 0) {
            item.child = filterChildren(child);
          }

          return hasPermission || (item.child && item.child.length > 0);
        });

        setMenuItems(validMenuData);

        const initialHideState = {};
        const initHideState = (items) => {
          items.forEach((item) => {
            if (item.child.length > 0) {
              initialHideState[item.menu.menuId.toString()] = false;
              initHideState(item.child);
            }
          });
        };
        initHideState(validMenuData);
        initialHideRef.current = { ...initialHideState };
        setHideState(initialHideState);
      } catch (error) {
        console.error("Error fetching menu:", error);
        setMenuItems([]);
      }
    };

    fetchMenu();
  }, []);

  const checkForActiveChildren = useCallback(
    (items) =>
      items.some(
        ({ menu, child }) =>
          menu &&
          Array.isArray(child) &&
          (pathname === menu.link ||
            pathname.startsWith(menu.link) ||
            (child.length > 0 && checkForActiveChildren(child)))
      ),
    [pathname]
  );

  const MemoizedSidenavCollapse = memo(SidenavCollapse);

  const renderMenu = useCallback(
    (items, level = 0) => (
      <MenuRenderer
        items={items}
        level={level}
        pathname={pathname}
        collapseName={collapseName}
        close={close}
        hideState={hideState}
        toggleHideState={toggleHideState}
        checkForActiveChildren={checkForActiveChildren}
        MemoizedSidenavCollapse={MemoizedSidenavCollapse}
        renderMenu={renderMenu}
        miniSidenav={miniSidenav}
      />
    ),
    [pathname, collapseName, close, hideState, checkForActiveChildren, toggleHideState]
  );

  const [forceUpdateKey, setForceUpdateKey] = useState(0);

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      if (window.innerWidth < 1200) {
        setSmallScreen(true);
        setClose(false);
      } else {
        setSmallScreen(false);
      }
    }

    function handleBrowserBack() {
      const localToken = getLocalToken();
      if (!localToken) navigate("/authentication/sign-in");
    }

    window.addEventListener("resize", handleMiniSidenav);
    window.addEventListener("popstate", handleBrowserBack);

    handleMiniSidenav();

    function handleStorage() {
      const localToken = getLocalToken();
      if (!localToken) {
        navigate("/authentication/sign-in");
        setLoading(dispatch, false);
      }
    }

    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("resize", handleMiniSidenav);
      window.removeEventListener("popstate", handleBrowserBack);
      window.removeEventListener("storage", handleStorage);
    };
  }, [dispatch, location, smallScreen]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (navRef.current && !navRef.current.contains(event.target) && close) {
        // Do nothing
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [navRef, close]);

  if (menuItems === null) {
    return (
      <SidenavRoot
        {...rest}
        variant="permanent"
        ownerState={{ transparentSidenav, miniSidenav }}
        className={`${close ? "close" : ""} ${miniSidenav ? "navContainerClose" : ""}`}
        ref={navRef}
      >
        <SuiBox pt={0} pb={1} pl={2} pr={8} textAlign="center">
          <Typography>Loading...</Typography>
        </SuiBox>
      </SidenavRoot>
    );
  }

  return (
    <SidenavRoot
      {...rest}
      style={{ backgroundColor: colors.sideBackground || "#F2F4F7" }}
      variant="permanent"
      ownerState={{ transparentSidenav, miniSidenav }}
      className={`${close ? "close" : ""} ${miniSidenav ? "navContainerClose" : ""}`}
      ref={navRef}
      key={forceUpdateKey}
      onMouseEnter={() => {
        if (isPinnedOpen) {
          setClose(false);
          setHideState(hideStateBeforeMini.current); // khôi phục trạng thái cũ
          setMiniSidenav(dispatch, false);
        }
      }}
      onMouseLeave={() => {
        if (isPinnedOpen) {
          hideStateBeforeMini.current = { ...hideState }; // lưu lại trạng thái hiện tại
          setClose(true);
          setMiniSidenav(dispatch, true);
        }
        if (isPinnedOpen) {
          // lưu lại trạng thái hiện tại
          hideStateBeforeMini.current = { ...hideState };

          // reset hideState để ẩn toàn bộ
          setHideState({});
          setClose(true);
          setMiniSidenav(dispatch, true);

          // ép cập nhật lại để re-render
          setForceUpdateKey((prev) => prev + 1);
        }
      }}
      color={colors.sideBackground || "#F2F4F7"}
    >
      <Icon
        onClick={() => {
          if (smallScreen) {
            setMiniSidenav(dispatch, true);
            setClose(false);
          } else {
            // Nếu đang hover mở tạm (close === false, miniSidenav === false, isPinnedOpen === true)
            if (isPinnedOpen && !close) {
              setIsPinnedOpen(false); // Ghim mở hoàn toàn
              setClose(false); // Giữ mở
              setMiniSidenav(dispatch, false); // Sidebar mở
              return;
            }

            const nextPinned = !isPinnedOpen;

            // if (!close && !nextPinned) {
            hideStateBeforeMini.current = { ...hideState }; // lưu trạng thái trước khi thu gọn
            // }

            setIsPinnedOpen(nextPinned);
            setClose(!close);
            setMiniSidenav(dispatch, true);
            setForceUpdateKey((prev) => prev + 1);
          }
        }}
        className="icon-open"
        sx={{
          marginRight: "-20px",
          marginTop: "12px",
          background: "white",
          color: "#3c7363",
          width: "25px",
          height: "25px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          "& .MuiIcon-root": {
            fontSize: "20px",
          },
        }}
      >
        keyboard_arrow_left
      </Icon>
      <SuiBox pl={3} pr={6} textAlign="center" className={close ? "logo-close" : ""}>
        <SuiBox display="flex" alignItems="center" justifyContent="center">
          {brand && (
            <SuiBox
              component="img"
              src={SampleLogo}
              alt="Logo"
              sx={{
                width: close || miniSidenav ? "56px" : "auto",
                maxWidth: close || miniSidenav ? "56px" : "150px",
                height: "auto",
                display: "block",
                transition: "all ease-in-out",
              }}
            />
          )}
        </SuiBox>
      </SuiBox>
      <SuiBox
        sx={{
          maxHeight: "calc(100vh - 120px)",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <List>{renderMenu(menuItems)}</List>
      </SuiBox>
    </SidenavRoot>
  );
}

Sidenav.defaultProps = { color: "info", brand: "" };
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  miniSidenav: PropTypes.bool.isRequired,
};

export default Sidenav;
